import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { errorTextkeys } from '~/components/limits/spending-limit/upload-docs/constants';
import { DocumentUploadType } from '~/components/limits/spending-limit/upload-docs/types';
import { uploadDocument } from '~/components/limits/spending-limit/upload-docs/utils';
import { NOTIFICATION_ID } from '~/components/limits/spending-limit/utils/constant';
import { FlowType, trackSpendingLimitEvent } from '~/components/limits/spending-limit/utils/tracking';
import { hideQueuedNotification, showQueuedNotification } from '~/components/notifications/ducks/actions';
import useTranslations from '~/components/translation/use-translations';
import { APIResponseStatuses } from '~/services/api-new';
import { useAppDispatch, useAppSelector } from '~/store/types';
import tempSessionIdGetter from '~/utils/deviceId';

type AuthenticationSlice = {
	email: string;
	password: string;
	isComplianceTermsAccepted: string;
};

const useUploadDoc = (flow: FlowType) => {
	const dispatch = useAppDispatch();
	const authentication = useAppSelector(state => state.authentication) as AuthenticationSlice;

	const credentials = useMemo(() => {
		const { email, password, isComplianceTermsAccepted = false } = authentication;
		const deviceId = tempSessionIdGetter();

		return {
			email,
			password,
			isComplianceTermsAccepted,
			deviceId
		};
	}, [authentication]);

	const [loading, setLoading] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const docs = useRef<DocumentUploadType[]>([]);

	const { translations: t } = useTranslations([...errorTextkeys, 'MOBILE_PENDING']);

	useEffect(() => {
		const cancelHandler = () => setLoading(false);

		document?.addEventListener('cancel', cancelHandler);

		return () => {
			document?.removeEventListener('cancel', cancelHandler);
		};
	}, []);

	const renderError = (message: string) => {
		trackSpendingLimitEvent({ flow, label: 'Click upload doc item failed' });

		dispatch(hideQueuedNotification(NOTIFICATION_ID));
		dispatch(
			showQueuedNotification({
				message,
				color: 'red',
				autoHide: true,
				openInSidebar: true,
				id: NOTIFICATION_ID
			})
		);
	};

	const onInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
		setLoading(true);

		if (event.target.files?.length) {
			try {
				const file = event.target.files[0];
				const response = await uploadDocument(file, credentials);

				if (response.status === APIResponseStatuses.SUCCESS) {
					if (response.result?.documentId) {
						docs.current = [
							{
								filename: file.name,
								playerTimestamp: Date.now(),
								status: t.MOBILE_PENDING,
								id: response.result?.documentId
							},
							...docs.current
						];

						trackSpendingLimitEvent({ flow, label: 'Click upload doc item successful' });

						return;
					}

					renderError(t.MOBILE_UPLOAD_DOCUMENTS_UPLOAD_API_ERROR);
					return;
				}

				renderError(response.errorDescription ?? t.MOBILE_UPLOAD_DOC_FILES_IS_GREATER_THEN_2MB);
			} catch (_) {
				renderError(t.MOBILE_UPLOAD_DOCUMENTS_UPLOAD_API_ERROR);
			} finally {
				setLoading(false);
			}
		} else {
			setLoading(false);
		}
	};

	const queryFn = () => {
		trackSpendingLimitEvent({ flow, label: 'Click upload doc item' });
		// setLoading(true);
		inputRef.current?.click();
	};

	return { loading, inputRef, onInputChange, queryFn, docs: docs.current };
};

export default useUploadDoc;
